import { compiledClientConfig } from "config";
import { devLog } from "utils/devtools/devLogging";
import { requirePropertyOf } from "utils/security";
import { graphqlReq } from "./graphqlReq";

interface IInsightsPageInfo {
	startCursor: string;
	endCursor: string;
	hasPreviousPage: boolean;
	hasNextPage: boolean;
}

interface IInsightsResults {
	nodes: unknown[];
	pageInfo: IInsightsPageInfo;
}

type TVariables = Record<string, unknown> & { limit?: number; endCursor?: string | null };

const MAX_PAGE_SIZE = 1000;
const { graphqlEndpoint } = compiledClientConfig.insights;

export const graphqlInsightsReq = async <T = unknown>(options: {
	query: string;
	variables: TVariables;
}): Promise<{ data: T }> => {
	if (!graphqlEndpoint) {
		devLog({ level: "error", message: "Insights graph endpoint not found" });
		return { data: {} as T };
	}
	const { query, variables } = options;

	if (!variables.limit) {
		variables.limit = MAX_PAGE_SIZE;
	}

	return graphqlReq<T>(graphqlEndpoint, { query, variables, suppressError: true });
};

// Insights data is limited to 1000 items per request, so we need to paginate through all the data
export const graphqlGetAllInsightsPages = async <T = unknown>(options: {
	query: string;
	variables: Omit<TVariables, "endCursor">;
	dataKey?: string;
}): Promise<T> => {
	let allData: unknown[] = [];
	let endCursor = null;
	const { query, variables } = options;
	const limit = typeof variables.limit === "number" ? variables.limit : MAX_PAGE_SIZE;

	while (true) {
		const { data } = await graphqlInsightsReq({
			query,
			variables: { ...variables, limit, endCursor }
		});

		if (!data) {
			throw new Error("Insights fetch error - no data returned");
		}
		const dataKey = options.dataKey || Object.keys(data)[0];
		if (!dataKey) {
			throw new Error("Insights fetch error - no data key found");
		}

		const { nodes, pageInfo } = requirePropertyOf(data, dataKey) as IInsightsResults;
		allData = allData.concat(nodes);

		if (!pageInfo.hasNextPage) {
			break;
		}
		endCursor = pageInfo.endCursor;
	}

	return allData as T;
};
